import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { readAuthSession } from "@/lib/auth-token"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import {
  AuthorSide,
  type FindingId,
  type Message,
  type MessageId,
} from "../data/review-data"

interface CommentWire {
  id: number
  review_id: number
  finding_id: number | null
  author_side: string
  author_id: number | null
  body: string
  created_at: string
  unread: boolean
}

interface ThreadWire {
  finding_id: number | null
  unread: number
  comments: CommentWire[]
}

interface ConversationOutWire {
  review_id: number
  side: string
  total_unread: number
  threads: ThreadWire[]
}

export interface ConversationData {
  messages: Message[]
  totalUnread: number
}

const AUTHOR_SIDES: readonly string[] = Object.values(AuthorSide)

function toAuthorSide(raw: string): AuthorSide {
  return AUTHOR_SIDES.includes(raw) ? (raw as AuthorSide) : AuthorSide.REVIEWER
}

// Names are intentionally role-only on the wire (DD-16); real identities
// live in the People sidebar.
function authorNameFor(side: AuthorSide): string {
  if (side === AuthorSide.AI) return "Pyxis AI"
  if (side === AuthorSide.CLIENT) return "Client"
  return "Reviewer"
}

function currentUserId(): number | null {
  const session = readAuthSession()
  if (!session) return null
  const parsed = Number(session.user.id)
  return Number.isFinite(parsed) ? parsed : null
}

function toMessage(c: CommentWire, myUserId: number | null): Message {
  const side = toAuthorSide(c.author_side)
  return {
    id: String(c.id) as MessageId,
    authorName: authorNameFor(side),
    authorSide: side,
    body: c.body,
    at: c.created_at,
    findingId: c.finding_id != null ? (String(c.finding_id) as FindingId) : null,
    mine: c.author_id != null && myUserId != null && c.author_id === myUserId,
  }
}

async function fetchComments(
  reviewId: string,
  signal: AbortSignal,
): Promise<ConversationData> {
  const wire = await http.get<ConversationOutWire>(
    `/api/v1/reviews/${encodeURIComponent(reviewId)}/comments`,
    { signal },
  )
  const my = currentUserId()
  const messages: Message[] = []
  for (const thread of wire.threads) {
    for (const comment of thread.comments) {
      messages.push(toMessage(comment, my))
    }
  }
  messages.sort((a, b) => a.at.localeCompare(b.at))
  return { messages, totalUnread: wire.total_unread }
}

export function useReviewCommentsQuery(
  reviewId: string | null,
): UseQueryResult<ConversationData> {
  return useQuery({
    queryKey: reviewId != null ? qk.reviews.comments(reviewId) : ["reviews", "comments", "none"],
    queryFn: ({ signal }) => fetchComments(reviewId as string, signal),
    enabled: reviewId != null,
  })
}
